import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';

import { kebabCase } from 'lodash';

import { DismissablePillButton } from '../../ButtonPillDropdown/PillButton';
import { EventsQuery, useEventsQuery } from '../../../generated/graphql';
import { PillDropdownOptions, PillWithRadioDropdown } from '../../ButtonPillDropdown';
import styles from './styles.scss';
import { SetFilterCriteria } from './useFilteredHouseholds';
import { MobileGuestFilters } from './mobile';

export const EVENTS_ALL_OPTION = 'All events';
export const RSVP_STATUS_ALL_OPTION = 'All guests';

export enum RSVP_STATUS {
  ACCEPTED = 'Accepted',
  NO_RESPONSE = 'No response',
  DECLINED = 'Declined',
}

export const RSVP_STATUS_OPTIONS = [
  { text: RSVP_STATUS_ALL_OPTION, value: RSVP_STATUS_ALL_OPTION },
  ...Object.values(RSVP_STATUS).map(status => ({ text: status, value: status })),
];
const DO_NOT_LIST_ALL_FILTERS = [EVENTS_ALL_OPTION, RSVP_STATUS_ALL_OPTION];

export type TrackGuestFilters = ({ action, selection }: { action: string; selection?: string | string[] }) => void;

export type FilterPresetState = { event: string | null; rsvpStatus: string | null };

export interface GuestFiltersProps {
  setFilterCriteria: SetFilterCriteria;
  filtersPath: string;
  trackGuestFilters: TrackGuestFilters;
  presetFilterState?: FilterPresetState;
}

export const GuestFilters = ({
  setFilterCriteria,
  filtersPath,
  trackGuestFilters,
  presetFilterState,
}: GuestFiltersProps) => {
  const { data: eventData } = useEventsQuery();
  const events = eventData?.events || [];
  const mobileMedia = useMobileMedia();
  const [eventFilter, setEventFilter] = useState<string | null>(presetFilterState?.event || null);
  const [rsvpStatusFilter, setRsvpStatusFilter] = useState<string | null>(presetFilterState?.rsvpStatus || null);

  const eventsOptionList: PillDropdownOptions = useMemo(
    () =>
      events?.reduce(
        (list: PillDropdownOptions, evt: EventsQuery['events'][0]) =>
          evt?.name ? [...list, { text: evt.name, value: evt.id }] : list,
        [{ text: EVENTS_ALL_OPTION, value: EVENTS_ALL_OPTION }]
      ),
    [events]
  );

  const eventIdToNameMap: Record<string, string> = useMemo(
    () => events?.reduce((obj, evt: EventsQuery['events'][0]) => ({ ...obj, [evt.id]: evt.name }), {}),
    [events]
  );
  const setEventFilterFromEventId = (eventId: string | null) =>
    setEventFilter(eventId ? eventIdToNameMap[eventId] : eventId);

  const addFilter = (
    setStateFunction: React.Dispatch<React.SetStateAction<string | null>> | ((value: string | null) => void)
  ) => {
    return (newValue: string) => {
      if (DO_NOT_LIST_ALL_FILTERS.includes(newValue)) {
        setStateFunction(null);
      } else {
        setStateFunction(newValue);
      }
    };
  };

  const trackRemoveFilter = useCallback(
    (clearChoice: string) => {
      trackGuestFilters({
        action: 'clear filter',
        selection: clearChoice,
      });
    },
    [trackGuestFilters]
  );

  const removeFilter = useCallback(
    (filterValue: string) => {
      if (filterValue === eventFilter) {
        setEventFilter(null);
        trackRemoveFilter(filterValue);
      } else if (filterValue === rsvpStatusFilter) {
        setRsvpStatusFilter(null);
        trackRemoveFilter(filterValue);
      }
    },
    [eventFilter, rsvpStatusFilter, trackRemoveFilter]
  );

  const clearFilters = useCallback(() => {
    setEventFilter(null);
    setRsvpStatusFilter(null);
    setFilterCriteria(null, null);
    trackRemoveFilter('clear all');
  }, [setFilterCriteria, trackRemoveFilter]);

  const handleMobileChanges = (rsvpString: string, eventString: string) => {
    setEventFilter(DO_NOT_LIST_ALL_FILTERS.includes(eventString) ? null : eventString);
    setRsvpStatusFilter(DO_NOT_LIST_ALL_FILTERS.includes(rsvpString) ? null : rsvpString);
    trackSaveClick();
  };

  const handleSetCriteria = useCallback(
    (rsvpStatus, event) => {
      setFilterCriteria(rsvpStatus, event);
    },
    [setFilterCriteria]
  );

  const trackPillClick = useCallback(() => {
    trackGuestFilters({
      action: 'open guest filters',
    });
  }, [trackGuestFilters]);

  const filters = useMemo(
    () => [eventFilter, rsvpStatusFilter].reduce((arr, val) => (!!val ? [...arr, val] : [...arr]), [] as string[]),
    [eventFilter, rsvpStatusFilter]
  );

  const trackSaveClick = useCallback(() => {
    trackGuestFilters({
      action: 'filter guests',
      selection: filters,
    });
  }, [filters, trackGuestFilters]);

  useEffect(() => {
    handleSetCriteria(rsvpStatusFilter, eventFilter);
  }, [eventFilter, handleSetCriteria, rsvpStatusFilter]);

  const showAppliedFilters = Boolean(eventFilter || rsvpStatusFilter);
  const eventFilterSelected = useMemo(() => !!eventFilter, [eventFilter]);
  const rsvpStatusFilterSelected = useMemo(() => !!rsvpStatusFilter, [rsvpStatusFilter]);
  const filtersState = { event: eventFilter, rsvpStatus: rsvpStatusFilter };

  return (
    <div className={styles.pillsContainer}>
      {mobileMedia.yes ? (
        <MobileGuestFilters
          filters={filtersState}
          events={events}
          setFilterCriteria={handleMobileChanges}
          filtersPath={filtersPath}
        />
      ) : (
        <div className={styles.pillsRow}>
          <PillWithRadioDropdown
            text="Events"
            options={eventsOptionList}
            defaultValue={EVENTS_ALL_OPTION}
            setValue={addFilter(setEventFilterFromEventId)}
            selected={eventFilterSelected}
            onPillClick={trackPillClick}
            onSaveClick={trackSaveClick}
            data-testid="events-filter-pill"
          />
          <PillWithRadioDropdown
            text="RSVP Status"
            options={RSVP_STATUS_OPTIONS}
            defaultValue={RSVP_STATUS_ALL_OPTION}
            setValue={addFilter(setRsvpStatusFilter)}
            selected={rsvpStatusFilterSelected}
            onPillClick={trackPillClick}
            onSaveClick={trackSaveClick}
            data-testid="rsvp-status-filter-pill"
          />
        </div>
      )}
      {showAppliedFilters && (
        <div className={styles.pillsRow}>
          {filters.map(appliedFilter => (
            <div key={`${appliedFilter}-pill`}>
              <DismissablePillButton
                data-testid={kebabCase(`filter-pill-${appliedFilter}`)}
                onClick={() => removeFilter(appliedFilter)}
              >
                {appliedFilter}
              </DismissablePillButton>
            </div>
          ))}
          <DisplayButton onClick={clearFilters} data-testid="clear-all-filters-button">
            Clear all
          </DisplayButton>
        </div>
      )}
    </div>
  );
};
